import { rainbowColors } from "@/pages/Dashboard/ComparisonTools";
import { colorSchemaList } from "@/utils/colorSchemas";
import { chartJsBorderDashMap, chartJsMarkerStyleMap, drawChartJsImage } from "@/utils/globalUtils";
import { CategoryScale, Chart as ChartJS, Legend, LineElement, PointElement, Title, Tooltip, LinearScale } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, annotationPlugin);

const hexToRgb = (hex) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

const alignItems = {
  left: "start",
  center: "center",
  right: "end",
};

const ChartJsBarLineCombo = ({
  data = [],
  colorSchema,
  dateFormat = "D/M/YY hh:mm a",
  lineDashType,
  markerType,
  markerColor,
  gridLine = true,
  verticalLine = true,
  legendPosition = "center bottom",
  threshold = [],
  showThumbsUp = false,
  tickGaps = 2,
}) => {
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);

  const [chartData, setChartData] = useState({ datasets: [] });

  const annotations = threshold.reduce((acc, { value, color }, index) => {
    if (value && color) {
      acc[`line${index}`] = {
        type: "line",
        yMin: value,
        yMax: value,
        borderColor: color,
        borderWidth: 2,
      };
    }
    return acc;
  }, {});

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 50,
      },
    },
    scales: {
      x: {
        position: "bottom",
        grid: {
          display: false,
        },
        border: {
          display: verticalLine,
        },
        ticks: {
          callback: function (value, index) {
            const label = this.getLabelForValue(value);
            if (typeof Number(tickGaps) === "number" && tickGaps > 0) {
              return index % tickGaps === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
            }
            return index % 2 === 0 ? moment(label, "DD/MM/YYYY HH:mm a").format(dateFormat) : undefined;
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: gridLine,
        },
        border: {
          display: verticalLine,
        },
      },
    },
    plugins: {
      annotation: { annotations },
      legend: {
        align: alignItems[legendPosition.split(" ")[0]] || "center",
        position: legendPosition.split(" ")[1] || "bottom",
      },
    },
    animation: {
      onComplete: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
      afterDraw: function ({ chart }) {
        if (showThumbsUp) {
          drawChartJsImage(chart);
        }
      },
    },
  };

  // effect
  useEffect(() => {
    const scheme = colorSchemaList.others?.find((item) => item?.value === colorSchema);
    const colors = scheme?.colors || rainbowColors;

    const chartInstance = chartRef.current;
    if (chartInstance) {
      const ctx = chartInstance.ctx;

      let datasetsBar = [];
      let datasetsLine = [];
      if (Array.isArray(data?.bar)) {
        datasetsBar = data?.bar?.map(({ chart, name, _id }, index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          const baseColor = colors[index % colors.length];

          gradient.addColorStop(0, `rgba(${hexToRgb(baseColor)}, 0.2)`);
          gradient.addColorStop(1, `rgba(${hexToRgb(baseColor)}, 1)`);

          return {
            type: "bar",
            label: name,
            data: Array.isArray(chart) ? chart.map((item) => ({ ...item, x: moment(item?.x).format("DD/MM/YYYY HH:mm a") })) : [],
            _id,
            backgroundColor: gradient,
            borderColor: baseColor,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            borderDash: chartJsBorderDashMap[lineDashType] || [],
            pointStyle: chartJsMarkerStyleMap[markerType] ?? "circle",
            pointBackgroundColor: markerColor,
            pointBorderColor: markerColor,
          };
        });
      }
      if (Array.isArray(data?.line)) {
        datasetsLine = data?.line?.map(({ chart, name, _id }, index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          const baseColor = colors[index % colors.length];

          gradient.addColorStop(0, `rgba(${hexToRgb(baseColor)}, 0.2)`);
          gradient.addColorStop(1, `rgba(${hexToRgb(baseColor)}, 1)`);

          return {
            type: "line",
            label: name,
            data: Array.isArray(chart) ? chart.map((item) => ({ ...item, x: moment(item?.x).format("DD/MM/YYYY HH:mm a") })) : [],
            _id,
            backgroundColor: gradient,
            borderColor: baseColor,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            borderDash: chartJsBorderDashMap[lineDashType] || [],
            pointStyle: chartJsMarkerStyleMap[markerType] ?? "circle",
            pointBackgroundColor: markerColor,
            pointBorderColor: markerColor,
          };
        });
      }
      setChartData({ datasets: [...datasetsBar, ...datasetsLine] });
    }
  }, [data, colorSchema]);

  return (
    <>
      <div ref={chartContainerRef} style={{ position: "relative", width: "100%", height: "calc(100%  - 30px)" }}>
        <Chart ref={chartRef} data={chartData} options={options} />
      </div>
    </>
  );
};

export default ChartJsBarLineCombo;

ChartJsBarLineCombo.propTypes = {
  data: PropTypes.array.isRequired,
  colorSchema: PropTypes.string,
  dateFormat: PropTypes.string,
  lineDashType: PropTypes.oneOf(["dot", "dash", "solid"]),
  markerType: PropTypes.oneOf(["square", "triangle", "cross", "none", "circle"]),
  markerColor: PropTypes.string,
  gridLine: PropTypes.bool,
  verticalLine: PropTypes.bool,
  legendPosition: PropTypes.string,
  threshold: PropTypes.array,
  showThumbsUp: PropTypes.bool,
  tickGaps: PropTypes.number,
};
